/**
 * L'EXEMPLE ANNOTÉ, dans la console — une copie corrigée au stylo (chantier
 * « manuel vivant », Φ-MV-2, MV-SAV-04).
 *
 * À gauche, la démarche. À droite, la marge : ce que le prof écrit, à la main.
 * Entre les deux, un trait d'encre part de chaque annotation et arrive sur le
 * MORCEAU EXACT de la formule dont elle parle (`\cible{nom}{…}`).
 *
 * ─── Ce qui est partagé avec l'application, et ce qui ne l'est pas ──────────
 *
 * PARTAGÉ (`@silex/shared`) : la boîte de chaque cible (`disposerMath`), le
 * tracé du trait, de sa marque et de sa pointe (`tracerAnnotation`), le
 * soulignement de la mise en page empilée (`soulignerCible`), le découpage de
 * l'écriture du prof (`decouperManuscrit`). Les constantes de mise en page
 * sont celles d'`ExempleAnnote.tsx`, recopiées à l'identique.
 *
 * PROPRE À LA CONSOLE : la lecture de deux positions à l'écran. L'application
 * les reçoit d'`onLayout` ; ici, elles se lisent dans le DOM après la mise en
 * page (`getBoundingClientRect`), et se relisent quand la colonne change de
 * largeur ou quand la police manuscrite finit d'arriver.
 *
 * ─── Colonne étroite ────────────────────────────────────────────────────────
 *
 * Quand la marge n'a plus la place d'une écriture, elle passe SOUS la ligne.
 * Aucun trait n'est alors tracé : la cible est soulignée, la note suit sa ligne.
 */
import {
  decouperManuscrit,
  tracerAnnotation,
  typographier,
  type BlocExempleAnnote,
  type TraitAnnotation,
} from '@silex/shared';
import { useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { TexteMaths } from '../TexteMaths';

import { IllustrationWeb } from './DessinWeb';
import { analyserFormule, ciblesEnPixels, FormuleWeb } from './FormuleWeb';

const TAILLE_FORMULE = 22;
const INTERLIGNE_MAIN = 25;
const COL_EGAL = 34;
const COL_EGAL_EMPILE = 30;
const DEMARCHE_GAUCHE = 10;
const DEMARCHE_DROITE = 34;
/** En dessous, la marge ne tient plus une écriture : on empile. */
const MARGE_MINIMALE = 230;

/** La main du prof : du texte, et ce que `^{…}` met en exposant. */
export function Manuscrit({ texte }: { readonly texte: string }): ReactNode {
  const morceaux = useMemo(() => decouperManuscrit(typographier(texte)), [texte]);
  const lisible = texte.replace(/\^\{([^}]*)\}/g, ' exposant $1');
  return (
    <span className="fiche-manuscrit" role="text" aria-label={lisible}>
      {morceaux.map((m, i) =>
        m.exposant ? (
          <sup key={i} aria-hidden="true">
            {m.texte}
          </sup>
        ) : (
          <span key={i} aria-hidden="true">
            {m.texte}
          </span>
        ),
      )}
    </span>
  );
}

interface TraitPose extends TraitAnnotation {
  readonly cle: string;
}

export function ExempleAnnoteWeb({
  bloc,
  pourRelecture = false,
}: {
  readonly bloc: BlocExempleAnnote;
  /** Écrit la description de la figure sous elle, pour le relecteur. */
  readonly pourRelecture?: boolean;
}): ReactNode {
  const copie = useRef<HTMLDivElement>(null);
  const [largeur, setLargeur] = useState(0);
  const [traits, setTraits] = useState<readonly TraitPose[]>([]);
  const [hauteur, setHauteur] = useState(0);

  const analysees = useMemo(() => bloc.lignes.map((l) => analyserFormule(l.tex)), [bloc.lignes]);
  const plusLarge = Math.max(
    0,
    ...analysees.map((a) => (a === null ? 0 : a.boite.largeur * TAILLE_FORMULE)),
  );
  const colDemarche = Math.max(largeur * 0.34, plusLarge + DEMARCHE_GAUCHE + DEMARCHE_DROITE);
  const empile = largeur > 0 && largeur - COL_EGAL - colDemarche < MARGE_MINIMALE;

  /* Les traits : de chaque annotation qui vise une cible, à cette cible. Tout
     se lit dans le repère de la copie. */
  const mesurer = useCallback(() => {
    const racine = copie.current;
    if (racine === null) return;
    const cadre = racine.getBoundingClientRect();
    const l = Math.floor(racine.clientWidth);
    setLargeur((avant) => (avant === l ? avant : l));
    setHauteur(Math.ceil(racine.scrollHeight));
    if (racine.dataset.mise !== 'marge') {
      setTraits((avant) => (avant.length === 0 ? avant : []));
      return;
    }
    const sortie: TraitPose[] = [];
    let rang = -1;
    bloc.lignes.forEach((ligne, i) => {
      ligne.notes?.forEach((note, j) => {
        rang += 1;
        const analysee = analysees[i];
        const formule = racine.querySelector(`[data-ligne="${i}"] .fiche-formule`);
        const ecrit = racine.querySelector(`[data-note="${i}-${j}"]`);
        if (note.vers === undefined || !analysee || formule === null || ecrit === null) return;
        const cible = ciblesEnPixels(analysee.boite, TAILLE_FORMULE).get(note.vers);
        if (cible === undefined) return;
        const f = formule.getBoundingClientRect();
        const n = ecrit.getBoundingClientRect();
        const fx = f.left - cadre.left;
        const fy = f.top - cadre.top;
        const par = note.par ?? 'dessous';
        /* D'où part le trait : le bord gauche de l'écriture, sur sa dernière
           ligne quand il plonge sous la formule, sur la première sinon. */
        const y =
          par === 'dessous'
            ? n.bottom - cadre.top - INTERLIGNE_MAIN * 0.44
            : n.top - cadre.top + INTERLIGNE_MAIN * 0.56;
        sortie.push({
          cle: `${i}-${j}`,
          ...tracerAnnotation(
            { g: fx + cible.g, d: fx + cible.d, h: fy + cible.h, b: fy + cible.b },
            [n.left - cadre.left - 7, y],
            note.marque ?? 'souligne',
            par,
            rang,
          ),
        });
      });
    });
    setTraits((avant) =>
      avant.length === sortie.length &&
      avant.every((t, k) => t.fleche === sortie[k]?.fleche && t.marque === sortie[k]?.marque)
        ? avant
        : sortie,
    );
  }, [bloc.lignes, analysees]);

  useLayoutEffect(() => {
    mesurer();
    const racine = copie.current;
    if (racine === null) return undefined;
    const observateur =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => mesurer());
    observateur?.observe(racine);
    /* La police manuscrite arrive après le premier rendu : une note change
       alors de hauteur, et son trait doit la suivre. */
    let vivant = true;
    void document.fonts?.ready.then(() => {
      if (vivant) mesurer();
    });
    return () => {
      vivant = false;
      observateur?.disconnect();
    };
    /* `colDemarche` : la première mesure donne la largeur, qui déplace la
       marge — les notes ne sont à leur place qu'au rendu suivant. */
  }, [mesurer, empile, colDemarche]);

  return (
    <div className="fiche-exemple">
      <p className="fiche-exemple__enonce">
        <TexteMaths texte={typographier(bloc.enonce)} />
      </p>
      {/* La figure de l'énoncé (`CAP-06`) : entre l'énoncé et la démarche. */}
      {bloc.figure !== undefined ? (
        <div className="fiche-exemple__figure">
          <IllustrationWeb illustration={bloc.figure} decritVisible={pourRelecture} />
        </div>
      ) : null}
      <div
        ref={copie}
        className={`fiche-copie ${empile ? 'fiche-copie--empilee' : 'fiche-copie--marge'}`}
        data-mise={empile ? 'empilee' : 'marge'}
        data-testid={empile ? 'exemple-empile' : 'exemple-en-marge'}
        style={{
          gridTemplateColumns: empile
            ? `${COL_EGAL_EMPILE}px 1fr`
            : `${COL_EGAL}px ${colDemarche}px 1fr`,
        }}
      >
        {bloc.lignes.map((ligne, i) => {
          const egal = (ligne.egal ?? i > 0) ? '=' : '';
          const notes = ligne.notes?.map((a, j) => (
            <span key={j} className="fiche-copie__note" data-note={`${i}-${j}`}>
              <Manuscrit texte={a.texte} />
            </span>
          ));
          return (
            <div key={i} className="fiche-copie__ligne" data-ligne={i}>
              <span className="fiche-copie__egal" aria-hidden="true">
                {egal}
              </span>
              <span className="fiche-copie__demarche">
                <FormuleWeb
                  tex={ligne.tex}
                  taille={TAILLE_FORMULE}
                  largeurMax={
                    empile
                      ? Math.max(0, largeur - COL_EGAL_EMPILE - 14 - DEMARCHE_GAUCHE)
                      : undefined
                  }
                  soulignees={
                    empile
                      ? (ligne.notes ?? []).flatMap((a) => (a.vers === undefined ? [] : [a.vers]))
                      : undefined
                  }
                />
              </span>
              <span className="fiche-copie__marge">{notes}</span>
            </div>
          );
        })}
        {traits.length > 0 ? (
          <svg
            className="fiche-copie__encre"
            width={largeur}
            height={hauteur}
            aria-hidden="true"
            data-testid="traits-du-prof"
          >
            {traits.map((t) => (
              <g key={t.cle}>
                {t.marque !== '' ? (
                  <path d={t.marque} className="fiche-plume fiche-plume--marque" />
                ) : null}
                <path d={t.fleche} className="fiche-plume" />
                <path d={t.pointe} className="fiche-plume" />
              </g>
            ))}
          </svg>
        ) : null}
      </div>
      {bloc.conclusion !== undefined ? (
        <p className="fiche-exemple__conclusion">
          <TexteMaths texte={typographier(bloc.conclusion)} />
        </p>
      ) : null}
    </div>
  );
}
